import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Award, Briefcase, Lightbulb, Users, ArrowUpRight } from "lucide-react";

const areasOfInterest = [
  {
    title: "Technology",
    description:
      "Architecting resilient, secure, and intelligent platforms that bring AI, data, and connected devices together for real-world healthcare outcomes.",
  },
  {
    title: "Administration",
    description:
      "Designing compliant operating models, orchestrating governance, and aligning commercial strategy with long-term business health.",
  },
  {
    title: "Team Building",
    description:
      "Cultivating cross-functional teams that thrive on mentorship, craftsmanship, and a shared sense of mission to deliver dependable products.",
  },
  {
    title: "Relations",
    description:
      "Building trusted partnerships with clinicians, research collaborators, investors, and policy makers to accelerate sustainable healthcare innovation.",
  },
];

const values = [
  {
    icon: Lightbulb,
    title: "Visionary Leadership",
    description:
      "Sees around the corner of change and translates future possibilities into clear, actionable roadmaps.",
  },
  {
    icon: Users,
    title: "People-First",
    description:
      "Builds collaborative teams rooted in empathy, mentorship, and a shared sense of purpose.",
  },
  {
    icon: Briefcase,
    title: "Operational Excellence",
    description:
      "Combines product thinking with disciplined execution to deliver dependable, scalable solutions.",
  },
  {
    icon: Award,
    title: "Integrity & Impact",
    description:
      "Measures success by the lasting impact on clinicians, patients, and communities everywhere.",
  },
];

const Founder = () => {
  return (
    <>
      <Layout>
      <main className="bg-background">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary to-secondary/80" />
          <div className="relative container mx-auto px-4 py-24 md:py-32 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center rounded-full border border-white/60 px-4 py-1 text-sm uppercase tracking-widest text-white/80">
                  Founder Story
                </span>
                <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Sumantra Mukherjee
                </h1>
                <p className="mt-4 text-lg md:text-xl text-white/90">
                  Founder, CEO & Chief Product Architect at Wellnext Technologies
                </p>
                <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
                  Sumantra Mukherjee brings over 30 years of experience in software engineering, spanning web and mobile development, cloud and enterprise architecture, large-scale databases, analytics, IoT, and embedded systems. With 25+ years of working with overseas clients, he has delivered robust, scalable solutions across multiple industries with a strong execution focus.
                </p>
                <p className="mt-2 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
                    An entrepreneur for nearly 20 years, he has built and delivered multiple successful products, owning the complete lifecycle from technical product management and architecture to team building, release management, versioning, and quality assurance. His approach emphasizes practical innovation, scalability, and long-term sustainability.
                </p>
                <p className="mt-2 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
                    In the health-tech domain, Sumantra has worked closely with medical research teams on data-intensive and analytics-driven projects. Beyond technology, he brings solid experience in commercial strategy, corporate and statutory compliance under MCA, along with hands-on recruitment experience across all organizational levels.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="text-base"
                    asChild
                  >
                    <a
                      href="https://www.linkedin.com/in/smukherjee1001"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Connect on LinkedIn
                    </a>
                  </Button>
                  <a
                    href="/Sumantra%20Mukherjee%20-%20Portfolio%20-%20CEO.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-md border border-white px-6 py-3 text-base font-medium text-white transition hover:bg-white/20"
                  >
                    Complete Bio
                    <ArrowUpRight className="ml-2 h-5 w-5" />
                  </a>
                </div>
              </div>

              <div className="flex justify-center lg:justify-end">
                <div className="relative w-64 h-84 md:w-72 md:h-114 lg:w-80 lg:h-126">
                  <div className="absolute inset-0 rounded-full bg-white/20 blur-3xl" />
                  <div className="relative h-full w-full overflow-hidden rounded-[2.5rem] border border-white/30 bg-white/10 backdrop-blur">
                    <img
                      src="/CEO_front_headshot.png"
                      alt="Portrait of Sumantra Mukherjee"
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4 text-white">
                      <p className="text-xs uppercase tracking-widest text-white/70">Founder & CEO</p>
                      <p className="text-base font-semibold">Sumantra Mukherjee</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Areas of Interest */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Areas of Interest</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                The disciplines that shape strategic decisions and keep Wellnext Technologies grounded in purpose-driven execution.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {areasOfInterest.map((item) => (
                <Card key={item.title} className="border-border hover:shadow-lg transition-transform duration-300 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-muted/60">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Guiding Principles</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Leadership that balances empathy with execution, ensuring Wellnext stays grounded in the values that matter most.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
              {values.map(({ icon: Icon, title, description }) => (
                <Card key={title} className="h-full border-border bg-background/70">
                  <CardContent className="p-6">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Personal Note */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <blockquote className="text-2xl md:text-3xl font-semibold text-primary leading-relaxed">
                "Healthcare deserves technology that empowers the people who dedicate their lives to healing. We build with humility, listen with intent, and deliver with precision."
              </blockquote>
              <p className="mt-6 text-muted-foreground">
                — Sumantra Mukherjee, Founder & Chief Product Architect, Wellnext Technologies
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-gradient-to-br from-primary via-primary/90 to-secondary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Work with Our Founder</h2>
            <p className="text-lg text-white/85 max-w-2xl mx-auto mb-8">
              Whether you are a healthcare leader, technology partner, or impact investor, let's collaborate to transform care delivery at scale.
            </p>
            <Button size="lg" variant="secondary" className="text-base">
              Schedule a Conversation
            </Button>
          </div>
        </section>
      </main>
    </Layout>
    </>
  );
};

export default Founder;
